<template>
  <b-modal
    :id="id"
    :title="$gettextInterpolate($gettext('Filters for %{layer}'), { layer: layerLabel })"
    size="lg"
    hide-footer
    @show="handleShow"
    ref="modal"
    static
  >
    <b-container fluid v-if="localFilters">
      <!-- Layout based rendering -->
      <template v-if="layout && layout.length">
        <b-row v-for="(row, rIdx) in layout" :key="'row-'+rIdx" class="mb-2">
          <b-col
            v-for="(col, cIdx) in row.row"
            :key="'col-'+cIdx"
            :md="col.span"
            class="mb-3"
          >
            <template v-if="col.keys">
              <div v-for="key in col.keys" :key="key" class="mb-3">
                <render-filter
                  v-if="filterDefinitions[key]"
                  :definition="filterDefinitions[key]"
                  :value="localFilters[key]"
                  @input="val => updateFilter(key, val)"
                />
              </div>
            </template>
            <template v-else-if="col.key">
              <render-filter
                v-if="filterDefinitions[col.key]"
                :definition="filterDefinitions[col.key]"
                :value="localFilters[col.key]"
                @input="val => updateFilter(col.key, val)"
              />
            </template>
          </b-col>
        </b-row>
      </template>

      <!-- Default grid rendering if no layout -->
      <b-row v-else>
        <b-col
          v-for="(def, key) in filterDefinitions"
          :key="key"
          md="6"
          class="mb-3"
        >
          <render-filter
            :definition="def"
            :value="localFilters[key]"
            @input="val => updateFilter(key, val)"
          />
        </b-col>
      </b-row>
    </b-container>

    <div class="modal-footer px-0 pb-0 pt-3 mt-3 border-top d-flex">
      <b-button variant="outline-danger" size="sm" @click="resetFilters" class="mr-auto">
        <font-awesome-icon icon="sync-alt" class="mr-1" />
        <translate>Reset</translate>
      </b-button>
      <b-button variant="secondary" size="sm" @click="$refs.modal.hide()" class="mr-2">
        <translate>Cancel</translate>
      </b-button>
      <b-button variant="primary" size="sm" @click="handleApply">
        <translate>Apply Filters</translate>
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { cloneDeep } from 'lodash'

export default {
  emits: ['apply'],
  name: 'LayerFiltersModal',
  components: {
    RenderFilter: {
      functional: true,
      props: ['definition', 'value'],
      render(h, { props, listeners }) {
        const { definition, value } = props
        const inputHandler = (val) => listeners.input(val)

        // Custom Component support
        if (definition.createComponent) {
          const componentCfg = definition.createComponent(value)
          return h(componentCfg.is, {
            props: {
              ...componentCfg,
              value
            },
            on: {
              input: inputHandler,
              // Support both value/input and modelValue/update:modelValue patterns
              'update:modelValue': inputHandler,
              // Support components that emit 'selected' (like CompanyAutocomplete)
              selected: inputHandler
            },
            ...componentCfg.attrs
          })
        }

        const normalizeValue = (val) => (val && typeof val === 'object') ? JSON.stringify(val) : val
        const denormalizeValue = (val) => {
          if (typeof val !== 'string') return val
          try {
            return JSON.parse(val)
          } catch (e) {
            return val
          }
        }

        // Standard Types
        switch (definition.type) {
          case 'text':
            return h('b-form-group', { props: { label: definition.label } }, [
              h('b-form-input', {
                props: { value, type: 'text', placeholder: definition.placeholder || '' },
                on: { input: inputHandler }
              })
            ])

          case 'select':
            return h('b-form-group', { props: { label: definition.label } }, [
              h('b-form-select', {
                props: { value, options: definition.options },
                on: { input: inputHandler }
              })
            ])

          case 'checkbox-group':
            return h('b-form-group', { props: { label: definition.label } }, [
              h('b-form-checkbox-group', {
                props: {
                  checked: (value || []).map(v => normalizeValue(v)),
                  options: definition.options.map(opt => ({ ...opt, value: normalizeValue(opt.value) })),
                  stacked: definition.stacked
                },
                on: { input: (vals) => inputHandler(vals.map(v => denormalizeValue(v))) }
              })
            ])

          case 'radio':
            return h('b-form-group', { props: { label: definition.label } }, [
              h('b-form-radio-group', {
                props: {
                  checked: normalizeValue(value),
                  options: definition.options.map(opt => ({ ...opt, value: normalizeValue(opt.value) })),
                  stacked: definition.stacked
                },
                on: { input: (val) => inputHandler(denormalizeValue(val)) }
              })
            ])

          case 'boolean':
            return h('b-form-checkbox', {
              props: { checked: value },
              on: { input: inputHandler },
              class: 'mt-4'
            }, definition.label)

          default:
            return h('div', `Unknown filter type: ${definition.type}`)
        }
      }
    }
  },
  props: {
    id: { type: String, required: true },
    layerKey: { type: String, required: true },
    layerLabel: { type: String, default: '' },
    filtersConfig: { type: Object, default: () => ({}) },
    currentFilters: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      localFilters: null
    }
  },
  computed: {
    layout() {
      return this.filtersConfig.layout || []
    },
    filterDefinitions() {
      return this.filtersConfig.items || {}
    }
  },
  methods: {
    handleShow() {
      // Initialize local copy from current state or defaults
      const initial = {}
      Object.keys(this.filterDefinitions).forEach(key => {
        const def = this.filterDefinitions[key]
        const fallbackValue = def.defaultValue !== undefined ? def.defaultValue : null
        initial[key] = this.currentFilters[key] !== undefined
          ? this.currentFilters[key]
          : fallbackValue
      })
      this.localFilters = cloneDeep(initial)
    },
    updateFilter(key, val) {
      this.$set(this.localFilters, key, val)
    },
    resetFilters() {
      const reset = {}
      Object.keys(this.filterDefinitions).forEach(key => {
        const def = this.filterDefinitions[key]
        reset[key] = def.defaultValue !== undefined ? def.defaultValue : null
      })
      this.localFilters = reset
    },
    handleApply() {
      this.$emit('apply', {
        layerKey: this.layerKey,
        filters: cloneDeep(this.localFilters)
      })
      this.$refs.modal.hide()
    }
  }
}
</script>

<style lang="scss">
#layerFiltersMapControlModal .modal-dialog {
  margin-top: 70px !important;
}
</style>